/**
 * Milestone title labels (MapMilestoneItem.vue's EllipsedText): the milestone's
 * title, right-aligned ending just left of its diamond and ellipsis-truncated to
 * the gap to the previous milestone. 11px / #333, at the same baseline as the
 * sequence caption (133, or 153 for mirrored). Hidden during the milestone
 * overlay mode, mirroring the SVG `v-if`.
 */
import { truncate } from '../../draw/text';
import { milestoneDim } from './MilestoneMarkersPainter';
import type { SceneContext } from '../../engine/SceneContext';

const GAP_WITH_PREV_MS = 20;

export function drawMilestoneLabels(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map, io } = scene;
  if (map.isMilestoneOverlay) {
    return;
  }

  ctx.font = '11px Roboto, sans-serif';
  ctx.fillStyle = '#333';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';

  for (const ms of io.milestones) {
    if (ms.isHiddenOnMap || map.isHiddenMirroredMilestone(ms.id) || !ms.title) {
      continue;
    }
    const msPos = map.milestonePos(ms);
    const endX = scene.screenX(msPos) - 15;
    // The label extends leftward from endX, so cull generously on the left.
    if (endX < -400 || endX > scene.width + 20) {
      continue;
    }

    const prevPos = ms.previousMsId
      ? map.milestonePos(io.milestone(ms.previousMsId))
      : 0;
    const maxWidth = map.scaleX(msPos - prevPos) - GAP_WITH_PREV_MS;
    if (maxWidth < 6) {
      continue;
    }

    const title = ms.mirrored
      ? `${ms.title} || ${ms.sourceProjectTitle ?? ''}`
      : ms.title;
    const { text } = truncate(ctx, title, maxWidth);
    ctx.globalAlpha = milestoneDim(scene, ms.id, !!ms.mirrored);
    ctx.fillText(text, endX, ms.mirrored ? 153 : 133);
  }
  ctx.globalAlpha = 1;
}
